import { warnAndNotify } from '@execonline-inc/error-reporting.private';
import { assertNever } from '@kofno/piper';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { Task } from 'taskarian';
import { callApi } from '../../../../../../Appy';
import { currentUserStore } from '../../../../../../CurrentUserStore';
import { findLinkT } from '../../../../../../LinkyLinky';
import { Link } from '../../../../../../Resource/Types';
import { participantOptionsResourceDecoder } from './Decoders';
import {
  ParticipantOptionError,
  ParticipantOptionsResource,
  ParticipantOptionsState,
  loading,
  ready,
} from './Types';

export class ParticipantOptionsStore {
  @observable state: ParticipantOptionsState = {
    kind: 'waiting',
  };

  constructor() {
    makeObservable(this);
  }

  @action
  load = () => {
    switch (this.state.kind) {
      case 'waiting':
        this.state = loading();
        this.fetchOptions();
        break;
      case 'loading':
      case 'ready':
      case 'error':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  reload = () => {
    this.state = { kind: 'waiting' };
    this.load();
  };

  private fetchOptions = () => {
    Task.succeed<ParticipantOptionError, ReadonlyArray<Link>>(currentUserStore.links)
      .andThen(findLinkT('v2-participant-options'))
      .andThen(callApi(participantOptionsResourceDecoder, {}))
      .fork(
        (err) => {
          warnAndNotify('ParticipantOptionsStore', 'Error loading participant options', {
            error: err,
          });

          runInAction(() => {
            this.state = {
              kind: 'error',
              message: 'Error loading options',
            };
          });
        },
        (resource: ParticipantOptionsResource) => {
          runInAction(() => {
            this.state = ready(resource);
          });
        },
      );
  };

  @computed
  get userGroups() {
    switch (this.state.kind) {
      case 'ready':
        return this.state.participantOptionsResource.payload.userGroups;
      case 'loading':
      case 'waiting':
      case 'error':
        return [];
      default:
        assertNever(this.state);
    }
  }

  @computed
  get licenses() {
    switch (this.state.kind) {
      case 'ready':
        return this.state.participantOptionsResource.payload.licenses;
      case 'loading':
      case 'waiting':
      case 'error':
        return [];
      default:
        assertNever(this.state);
    }
  }

  @computed
  get canAddAsAuditor() {
    switch (this.state.kind) {
      case 'ready':
        return this.state.participantOptionsResource.payload.canAddAsAuditor;
      case 'loading':
      case 'waiting':
      case 'error':
        return false;
      default:
        assertNever(this.state);
    }
  }
}

const participantOptionsStore = new ParticipantOptionsStore();

export { participantOptionsStore };
